import { cookies } from '@utils/cookies';
import { useState } from 'react';

type DownloadParams = {
    relativePath?: string;
    absolutePath?: string;
    body?: Record<string | number, unknown>;
    method?: string;
};

const getDownloadApiPath = ({ relativePath, absolutePath }: Pick<DownloadParams, 'relativePath' | 'absolutePath'>) => {
    if (absolutePath) {
        return absolutePath;
    }

    if (!relativePath) {
        throw new Error('Either relativePath or absolutePath must be provided');
    }

    if (relativePath.startsWith('/api-professional/')) {
        return relativePath;
    }

    return `/api-professional/${relativePath}`;
};

const useLazyDownload: () => [
    (params: DownloadParams) => Promise<Blob>,
    { isLoading: boolean; error: unknown | null }
] = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown | null>(null);

    const downloadData = async ({ relativePath, absolutePath, body, method }: DownloadParams) => {
        setIsLoading(true);
        try {
            const response = await fetch(getDownloadApiPath({ relativePath, absolutePath }), {
                method: method || 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': cookies.get('csrftoken') ?? '',
                },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw { data: errorData, status: response.status };
                } else {
                    throw new Error('Failed to download PDF');
                }
            }
            const filename =
                response.headers.get('content-disposition')?.split('filename=')[1].split(';')[0].replace(/"/g, '') ??
                'file.pdf';
            const blob = await response.blob();
            const url = window.URL.createObjectURL(new Blob([blob]));

            // Create a link element, set the URL as the href and trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Cleanup the URL and link element
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
            setIsLoading(false);
            setError(null);

            return blob;
        } catch (error) {
            setError(error);
            setIsLoading(false);
            throw error;
        }
    };

    return [downloadData, { isLoading, error }];
};

export { useLazyDownload };
